
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  ReactNode,
  useEffect
} from 'react';
import axios, { AxiosInstance } from 'axios';
import { isTokenExpired, extractUserFromToken, verifyToken } from '@/utils/tokenUtils';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import { useAxiosInterceptors } from '@/hooks/useAxiosInterceptors';
import { AuthContextShape, User } from '@/utils/types';
import { readToken, clearTokens, getRefreshToken } from '@/utils/readToken';
import { toast } from 'sonner';
import { applyWebsiteScopeHeaders, getWebsiteSlug } from '@/lib/websiteScope';
import { PUBLIC_API_BASE_URL } from '@/utils/publicApiConfig';

const API_BASE_URL = PUBLIC_API_BASE_URL;
const ACCESS_TOKEN_NAME = 'admin_access_token';
const REFRESH_TOKEN_NAME = 'admin_refresh_token';

const AuthContext = createContext<AuthContextShape | undefined>(undefined);

export const useAuth = (): AuthContextShape => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  const apiRef = useRef<AxiosInstance>(
    axios.create({
      baseURL: API_BASE_URL,
    })
  );
  const api = apiRef.current;

  const logout = useCallback(() => {
    clearTokens();
    setIsAuthenticated(false);
    setUser(null);
    clearRefreshTimer();
  }, []);

  const { refreshAccessToken, scheduleSilentRefresh, clearRefreshTimer } = useTokenRefresh({
    api,
    setUser,
    setIsAuthenticated,
    logout,
  });

  // Use our new interceptors hook
  useAxiosInterceptors(api, refreshAccessToken);

  // Check if user is allowed to access a specific website
  const checkUserAllowed = useCallback(async (websiteSlug: string, accessToken: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/user-allowed/${websiteSlug}/`, {
        headers: applyWebsiteScopeHeaders({
          Authorization: `Bearer ${accessToken}`,
        }),
      });
      return response.data.allowed;
    } catch (error) {
      console.error("Failed to verify user access:", error);
      return false;
    }
  }, []);

  const login = useCallback(
    async (access: string, refresh: string, userOverride?: Partial<User>) => {
      // Set tokens and headers immediately for subsequent API calls
      localStorage.setItem(ACCESS_TOKEN_NAME, access);
      localStorage.setItem(REFRESH_TOKEN_NAME, refresh);
      api.defaults.headers.common['Authorization'] = `Bearer ${access}`;

      // If websiteSlug is provided, check if user is allowed
      const isAllowed = await checkUserAllowed(getWebsiteSlug(), access);
      if (!isAllowed) {
        // User is not allowed, clean up and prevent login
        logout();
        toast.error("You don't have permission to access this website");
        return false;
      }

      // Proceed with login
      const decoded = extractUserFromToken(access);
      setUser({ ...decoded, ...userOverride } as User);
      setIsAuthenticated(true);
      scheduleSilentRefresh(access);
      return true;
    },
    [api, scheduleSilentRefresh, checkUserAllowed, logout]
  );

  // Initialize auth state
  useEffect(() => {
    const checkAuth = async () => {
      const access = readToken();
      const refresh = getRefreshToken();

      if (!access) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        // Verify token with backend
        const isValid = await verifyToken(access);
        
        if (isValid && !isTokenExpired(access)) {
          api.defaults.headers.common['Authorization'] = `Bearer ${access}`;
          setUser(extractUserFromToken(access));
          setIsAuthenticated(true);
          scheduleSilentRefresh(access);
        } else if (refresh) {
          try {
            await refreshAccessToken(refresh);
          } catch (refreshError) {
            console.error("Failed to refresh token:", refreshError);
            logout();
          }
        } else {
          logout();
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, [api, refreshAccessToken, scheduleSilentRefresh, logout]);

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    api,
    checkUserAllowed
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
